import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Building2, Loader2, CheckCircle, Clock, Mail, User, FileText,
  UserMinus, Briefcase, Calendar, AlertTriangle, X, ChevronRight
} from 'lucide-react';

const statusConfig = {
  invited: { label: 'Invited', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Mail },
  onboarding: { label: 'Onboarding', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: User },
  active_employee: { label: 'Active Employee', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Briefcase },
  former_employee: { label: 'Former Employee', color: 'bg-[#F5F8FF] text-[#5B6472] border-[#E8EDF5]', icon: Clock },
  record_pending: { label: 'Record Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  record_verified: { label: 'Record Verified', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
};

const Connected = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const res = await employeeAPI.getConnections();
      setConnections(res.data || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveConnection = async () => {
    if (!selectedConnection) return;
    
    setRemoving(true);
    try {
      await employeeAPI.removeConnection(selectedConnection.id);
      toast.success('Connection removed');
      setRemoveModalOpen(false);
      setSelectedConnection(null);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove connection');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="connected-page">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] flex items-center gap-2">
            <Building2 className="w-8 h-8 text-[#1B5AFF]" />
            Connected Companies
          </h1>
          <p className="mt-2 text-[#5B6472]">Companies you're linked to on VETT</p>
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap gap-3 animate-fade-in">
          {Object.entries(statusConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${config.color.split(' ')[0]}`} />
              <span className="text-[#5B6472] text-xs">{config.label}</span>
            </div>
          ))}
        </div>

        {/* Connections List */}
        {connections.length === 0 ? (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center animate-fade-in">
            <Building2 className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No connections yet</h3>
            <p className="text-[#5B6472]">
              When employers invite you or create VETT records for you, they'll appear here.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {connections.map((connection, i) => {
              const status = statusConfig[connection.status] || statusConfig.invited;
              const hasVerifiedRecord = connection.status === 'record_verified';
              
              return (
                <Card 
                  key={connection.id}
                  className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5 animate-fade-in cursor-pointer hover:border-[#1B5AFF]/50 transition-colors"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`connection-${connection.id}`}
                  onClick={() => navigate(`/employee/company/${connection.employer_id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {connection.company_logo ? (
                        <img 
                          src={connection.company_logo} 
                          alt="" 
                          className="w-14 h-14 rounded-xl object-cover" 
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-[#5B6472]" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-[#0A0F1E] text-lg">{connection.company_name}</h3>
                        {connection.job_title && (
                          <p className="text-[#1B5AFF] text-sm">{connection.job_title}</p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-[#5B6472] text-xs">
                          {connection.department && (
                            <span>{connection.department}</span>
                          )}
                          {connection.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {connection.start_date}
                            </span>
                          )}
                          {connection.industry && (
                            <span>{connection.industry}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Status Badge */}
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${status.color}`}>
                        <status.icon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                      
                      {/* Remove Button - only if not a verified record */}
                      {!hasVerifiedRecord && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedConnection(connection);
                            setRemoveModalOpen(true);
                          }}
                          className="text-[#5B6472] hover:text-red-400 hover:bg-red-500/10 rounded-lg h-8 w-8 p-0"
                          title="Remove connection"
                          data-testid={`remove-connection-${connection.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Remove Connection Modal */}
        <Dialog open={removeModalOpen} onOpenChange={setRemoveModalOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserMinus className="w-5 h-5 text-red-400" />
                Remove Connection
              </DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                Are you sure you want to remove this connection? This will hide the company from your connected list, but any verified records will remain intact.
              </DialogDescription>
            </DialogHeader>
            
            {selectedConnection && (
              <div className="bg-[#F5F8FF] rounded-xl p-4 border border-[#E8EDF5]">
                <div className="flex items-center gap-3">
                  {selectedConnection.company_logo ? (
                    <img src={selectedConnection.company_logo} alt="" className="w-10 h-10 rounded-lg" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#FFFFFF] flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#5B6472]" />
                    </div>
                  )}
                  <div>
                    <p className="text-[#0A0F1E] font-medium">{selectedConnection.company_name}</p>
                    {selectedConnection.job_title && (
                      <p className="text-[#5B6472] text-sm">{selectedConnection.job_title}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setRemoveModalOpen(false)}
                className="text-[#5B6472]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemoveConnection}
                disabled={removing}
                className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] rounded-xl"
              >
                {removing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Remove Connection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Connected;
